import { useMemo, useCallback } from "react";
import { CropFilters } from "../_types";

const T_SEPARATOR = "_";

interface UseCropsFiltersProps {
  urlParams: Record<string, string>;
  onFilterChange?: (
    key: string,
    value: string | number | boolean | undefined | null
  ) => void;
  onFiltersChange: (
    updates: Record<string, string | number | boolean | undefined | null>
  ) => void;
  onFiltersClear: () => void;
}

export function useCropsFilters({
  urlParams,
  // onFilterChange,
  onFiltersChange,
  onFiltersClear,
}: UseCropsFiltersProps) {
  // Parse filters from URL params
  const filters = useMemo((): CropFilters => {
    const typeIdParam = urlParams.typeId;
    const typeIds = typeIdParam
      ? typeIdParam
          .split(T_SEPARATOR)
          .map(Number)
          .filter((id) => !isNaN(id))
      : [];

    const res = getStrOptions(urlParams);
    const numRes = getNumOptions(urlParams);

    return {
      ...res,
      ...numRes,
      isFeatured: urlParams.isFeatured === "true" ? true : undefined,
      typeId: typeIds.length > 0 ? typeIds : undefined,
      sortBy: urlParams.sortBy || "recent",
    } as CropFilters;
  }, [urlParams]);

  // Update a single filter
  const updateFilter = useCallback(
    <K extends keyof CropFilters>(key: K, value: CropFilters[K]) => {
      if (["undefined", "null", "", "all"].includes(String(value))) {
        onFiltersChange({ [key]: undefined, page: undefined });
      } else if (key === "sortBy" && value === "recent") {
        onFiltersChange({ [key]: undefined, page: undefined });
      } else if (Array.isArray(value)) {
        if (value.length > 0) {
          onFiltersChange({ [key]: value.join(T_SEPARATOR), page: undefined });
        } else {
          onFiltersChange({ [key]: undefined, page: undefined });
        }
      } else {
        onFiltersChange({ [key]: value, page: undefined });
      }
    },
    [onFiltersChange]
  );

  // Reset all filters
  const resetFilters = useCallback(() => {
    onFiltersClear();
  }, [onFiltersClear]);

  // Toggle crop type (for multi-select)
  const toggleCropType = useCallback(
    (typeId: number) => {
      const currentTypes = filters.typeId || [];
      const newTypes = currentTypes.includes(typeId)
        ? currentTypes.filter((id) => id !== typeId)
        : [...currentTypes, typeId];

      updateFilter("typeId", newTypes.length > 0 ? newTypes : undefined);
    },
    [filters.typeId, updateFilter]
  );

  const setPriceRange = useCallback(
    (min: number, max: number) => {
      const updates: Record<string, number | undefined> = { page: undefined };

      if (min > 0) {
        updates.priceFrom = min;
      } else {
        updates.priceFrom = undefined;
      }

      const MAX_PRICE = 15000000;
      if (max < MAX_PRICE) {
        updates.priceTo = max;
      } else {
        updates.priceTo = undefined;
      }

      onFiltersChange(updates);
    },
    [onFiltersChange]
  );

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return Object.entries(filters).some(([key, value]) => {
      if (key === "sortBy") return value !== "recent";
      if (Array.isArray(value)) return value.length > 0;
      return value !== undefined && value !== null && value !== "";
    });
  }, [filters]);

  // Convert filters to API query format
  const apiFilters = useMemo(() => {
    const result: Record<string, string> = {};

    Object.entries(filters).forEach(([key, value]) => {
      if (value === undefined || value === null || value === "") return;
      if (key === "sortBy" && value === "recent") return;

      if (Array.isArray(value)) {
        if (value.length > 0) {
          result[key] = value.join(",");
        }
      } else {
        result[key] = String(value);
      }
    });

    return result;
  }, [filters]);

  return {
    filters,
    apiFilters,
    updateFilter,
    resetFilters,
    toggleCropType,
    setPriceRange,
    hasActiveFilters,
  };
}

export type UseCropsFilters = ReturnType<typeof useCropsFilters>;
// Basically doing searchParams.get(option) for each option
function getStrOptions(urlParams: Record<string, string>) {
  const strOptions = [
    "status",
    "certificationType",
    "country",
    "city",
    "state",
    "farmerId",
    "search",
  ];
  return strOptions.reduce(
    (acc, option) => {
      acc[option] = urlParams[option] || undefined;
      return acc;
    },
    {} as Record<string, string | undefined>
  );
}

function getNumOptions(urlParams: Record<string, string>) {
  const numberOptions = ["priceFrom", "priceTo", "sellerRatingFrom"];
  return numberOptions.reduce(
    (acc, option) => {
      const value = Number(urlParams[option]);
      acc[option] = value && !isNaN(value) ? value : undefined;
      return acc;
    },
    {} as Record<string, number | undefined>
  );
}
